import React from "react";
import { useLang } from "../lib/lang-context";
import { MapPin, Path } from "@phosphor-icons/react";

export default function Coverage() {
  const { t } = useLang();
  return (
    <section
      id="coverage"
      data-testid="coverage-section"
      className="py-24 md:py-32 bg-[var(--icor-navy)] text-white relative overflow-hidden"
    >
      <div className="absolute inset-0 bg-grid opacity-30" aria-hidden />
      <div
        className="absolute inset-0 opacity-50"
        style={{
          background:
            "radial-gradient(50% 50% at 80% 20%, rgba(0,82,255,0.25), transparent 60%)",
        }}
        aria-hidden
      />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <p className="text-xs tracking-[0.24em] uppercase font-bold text-blue-300">
              <span className="inline-block w-7 h-px bg-blue-300 align-middle mr-3" />
              {t.coverage.eyebrow}
            </p>
            <h2 className="font-display text-4xl md:text-5xl font-semibold tracking-tighter mt-5 text-balance">
              {t.coverage.title}
            </h2>
            <p className="mt-6 text-lg text-white/70 leading-relaxed">
              {t.coverage.subtitle}
            </p>

            <div className="mt-10">
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-white/50 mb-4">
                {t.coverage.hubsTitle}
              </div>
              <ul className="space-y-3">
                {t.coverage.hubs.map((h, i) => (
                  <li
                    key={i}
                    data-testid={`coverage-hub-${i}`}
                    className="flex items-start gap-4 group"
                  >
                    <div
                      className="mt-1 w-8 h-8 flex items-center justify-center flex-shrink-0 transition-colors group-hover:bg-[var(--icor-blue)]"
                      style={{
                        background: "rgba(255,255,255,0.06)",
                        borderRadius: 4,
                      }}
                    >
                      <MapPin size={14} weight="bold" />
                    </div>
                    <div className="flex-1 border-b border-white/10 pb-3">
                      <div className="font-medium tracking-tight">{h.city}</div>
                      <div className="text-xs text-white/50 mt-0.5">
                        {h.role}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="lg:col-span-7">
            {/* Stylized "map" with gradient + country tiles */}
            <div
              className="relative aspect-[4/3] border border-white/10 overflow-hidden"
              style={{
                background:
                  "linear-gradient(180deg, rgba(0,82,255,0.08) 0%, rgba(0,0,0,0.4) 100%)",
                borderRadius: 4,
              }}
              data-testid="coverage-map"
            >
              {/* Topographic line accents */}
              <svg
                className="absolute inset-0 w-full h-full opacity-30"
                viewBox="0 0 600 450"
                preserveAspectRatio="none"
                aria-hidden
              >
                <defs>
                  <linearGradient id="lineGrad" x1="0" x2="1">
                    <stop offset="0%" stopColor="#1463ff" stopOpacity="0" />
                    <stop offset="50%" stopColor="#1463ff" stopOpacity="1" />
                    <stop offset="100%" stopColor="#1463ff" stopOpacity="0" />
                  </linearGradient>
                </defs>
                <path
                  d="M50,90 Q200,40 380,120 T580,80"
                  stroke="url(#lineGrad)"
                  strokeWidth="1"
                  fill="none"
                />
                <path
                  d="M30,180 Q220,140 410,220 T590,180"
                  stroke="url(#lineGrad)"
                  strokeWidth="1"
                  fill="none"
                />
                <path
                  d="M40,280 Q210,250 400,320 T590,280"
                  stroke="url(#lineGrad)"
                  strokeWidth="1"
                  fill="none"
                />
                <path
                  d="M30,380 Q200,340 380,410 T590,360"
                  stroke="url(#lineGrad)"
                  strokeWidth="1"
                  fill="none"
                />
              </svg>

              {/* Pulse dots representing hubs */}
              {[
                { x: "22%", y: "30%", label: "Toronto" },
                { x: "30%", y: "55%", label: "El Paso" },
                { x: "42%", y: "70%", label: "MTY" },
                { x: "48%", y: "82%", label: "GDL" },
                { x: "55%", y: "85%", label: "CDMX" },
              ].map((p, i) => (
                <div
                  key={i}
                  className="absolute"
                  style={{ left: p.x, top: p.y, transform: "translate(-50%,-50%)" }}
                >
                  <span className="block w-3 h-3 rounded-full bg-[var(--icor-blue)] shadow-[0_0_0_6px_rgba(0,82,255,0.18)] animate-pulse" />
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[10px] tracking-[0.18em] uppercase font-semibold text-white/80 whitespace-nowrap">
                    {p.label}
                  </span>
                </div>
              ))}

              <div className="absolute bottom-4 right-4 flex items-center gap-2 text-[10px] tracking-[0.2em] uppercase text-white/40">
                <Path size={12} /> Norteamérica
              </div>
            </div>

            {/* Country chips */}
            <div className="mt-6 grid grid-cols-3 gap-3">
              {t.coverage.countries.map((c, i) => (
                <div
                  key={c.code}
                  data-testid={`coverage-country-${c.code}`}
                  className="bg-white/5 border border-white/10 p-4 hover:bg-white/10 transition-colors"
                  style={{ borderRadius: 4 }}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-xl" aria-hidden>
                      {c.flag}
                    </span>
                    <span className="font-display font-semibold tracking-tight">
                      {c.name}
                    </span>
                  </div>
                  <div className="mt-2 text-xs text-white/50">{c.coverage}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
